import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Shield, Users, Plane, FileText, Inbox, Globe, BookOpen, Download,
  LogOut, Search, Trash2, Star, Check, X, RefreshCw, Eye, EyeOff,
  Mail, TrendingUp, Clock, AlertCircle, Filter, ExternalLink,
  Euro, CreditCard, Send, RotateCcw, CalendarDays, KeyRound, Plane as PlaneIcon, Lock, Edit,
  Layers, Zap, Link2,
} from 'lucide-react';
import { ChronoDashboardTab, BlogPostsTab, SEOPagesTab, ContentQueueTab, EntitiesTab, AuditTab, PlanGeneratorTab, ChronoCronTab, ChronoRedirectsTab, ChronoAndamentoTab } from './admin/ChronoTabs';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'aerodeals_admin_token';

/* ─── Axios helpers ─── */
const authHeaders = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const fmtPrice = (n) => (n == null ? '—' : `€${Number(n).toLocaleString('it-IT')}`);
const fmtEur = (n) => `€${Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* ─── Noindex helper ─── */
function useNoIndex(title) {
  useEffect(() => {
    document.title = title;
    let m = document.querySelector('meta[name="robots"]');
    if (!m) {
      m = document.createElement('meta');
      m.setAttribute('name', 'robots');
      document.head.appendChild(m);
    }
    m.setAttribute('content', 'noindex, nofollow, noarchive');
    return () => m && m.setAttribute('content', 'index, follow');
  }, [title]);
}

/* ─── Login Gate ─── */
function LoginGate({ onAuth }) {
  const [pwd, setPwd] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password: pwd });
      localStorage.setItem(TOKEN_KEY, data.token);
      onAuth();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Accesso negato');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-4" data-testid="admin-login">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center mb-8">
          <div className="w-14 h-14 flex items-center justify-center bg-[#C5A059]/15 border border-[#C5A059]/30">
            <Shield size={24} className="text-[#C5A059]" />
          </div>
        </div>
        <h1 className="text-2xl text-white text-center mb-2 tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          AeroDeals Admin
        </h1>
        <p className="text-xs text-zinc-500 text-center mb-8 font-mono uppercase tracking-widest">Accesso riservato</p>
        <form onSubmit={submit} className="bg-zinc-900/60 border border-zinc-800 p-6 space-y-4">
          <div>
            <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Password</label>
            <div className="relative">
              <input
                type={show ? 'text' : 'password'}
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                required
                autoFocus
                className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 pr-10 text-sm text-white outline-none focus:border-[#C5A059]"
                data-testid="admin-pwd-input"
              />
              <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                {show ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#C5A059] text-black py-2.5 font-medium text-sm hover:bg-[#D4B271] transition-colors disabled:opacity-60"
            data-testid="admin-login-btn"
          >
            {loading ? 'Verifica...' : 'Entra'}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ─── Confirm helper ─── */
const confirmAction = (msg) => window.confirm(msg);

/* ─── TABS ─── */
const TABS = [
  { id: 'overview', label: 'Overview', icon: TrendingUp },
  { id: 'transactions', label: 'Transazioni', icon: CreditCard },
  { id: 'users', label: 'Utenti', icon: Users },
  { id: 'listings', label: 'Annunci', icon: Plane },
  { id: 'claims', label: 'Rivendicazioni', icon: FileText },
  { id: 'requests', label: 'Richieste', icon: Inbox },
  { id: 'emails', label: 'Email Template', icon: Mail },
  { id: 'chrono', label: 'Chrono SEO', icon: Zap, section: true },
  { id: 'andamento', label: 'Andamento', icon: TrendingUp },
  { id: 'chrono-advanced', label: 'Avanzato ▸', icon: Layers, advancedToggle: true },
  { id: 'audit', label: 'Audit Contenuti', icon: Search, advanced: true },
  { id: 'blog', label: 'Articoli Blog', icon: BookOpen, advanced: true },
  { id: 'seo', label: 'Pagine SEO', icon: Globe, advanced: true },
  { id: 'content-queue', label: 'Content Queue', icon: CalendarDays, advanced: true },
  { id: 'plan-generator', label: 'Piano 500gg', icon: PlaneIcon, advanced: true },
  { id: 'chrono-cron', label: 'Impostazioni Cron', icon: Clock, advanced: true },
  { id: 'redirects', label: 'Redirect 301', icon: Link2, advanced: true },
  { id: 'entities', label: 'Entità', icon: Layers, advanced: true },
  { id: 'scraper', label: 'Scraper', icon: Download, soon: true, advanced: true },
];

/* ─── Overview Tab ─── */
function OverviewTab() {
  const [stats, setStats] = useState(null);
  const [rev, setRev] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: authHeaders() }),
        axios.get(`${API}/admin/revenue`, { headers: authHeaders() }),
      ]);
      setStats(s.data);
      setRev(r.data);
    } catch { toast.error('Errore caricamento overview'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!stats || !rev) return <div className="text-zinc-500 text-sm">Caricamento...</div>;

  const kpis = [
    { label: 'Utenti totali', value: stats.users, sub: `+${stats.new_users_7d} ultimi 7gg`, icon: Users },
    { label: 'Annunci attivi', value: stats.listings_active, sub: `${stats.listings_total} totali`, icon: Plane },
    { label: 'In Evidenza', value: stats.listings_featured, sub: 'Featured attivi', icon: Star, accent: true },
    { label: 'Nuovi Annunci 7gg', value: stats.new_listings_7d, sub: 'Crescita', icon: TrendingUp },
    { label: 'Rivendicazioni', value: stats.claims_pending, sub: 'In attesa', icon: FileText, alert: stats.claims_pending > 0 },
    { label: 'Nuove Richieste', value: stats.requests_new, sub: 'Da gestire', icon: Inbox, alert: stats.requests_new > 0 },
    { label: 'LOI Pending', value: stats.loi_pending, sub: 'In attesa pagamento', icon: Download, alert: stats.loi_pending > 0 },
    { label: 'Messaggi totali', value: stats.messages_total, sub: 'Tutti utenti', icon: Mail },
  ];

  const periods = [
    { id: 'today', label: 'Oggi' },
    { id: 'week', label: '7 giorni' },
    { id: 'month', label: '30 giorni' },
    { id: 'year', label: '12 mesi' },
    { id: 'all', label: 'Totale' },
  ];

  const PRODUCT_LABELS = {
    listing_base: 'Annuncio Base · €4,90',
    listing_pro: 'Annuncio Pro · €14,90',
    listing_premium: 'Annuncio Premium · €39,90',
    featured: 'In Evidenza · €14,90',
    loi: 'Lettera di Intenti · €9,90',
  };

  // Chart - simple SVG bar chart
  const maxDaily = Math.max(1, ...rev.daily_30d.map((d) => d.amount));
  const barW = 100 / rev.daily_30d.length;

  return (
    <div data-testid="admin-overview">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl text-white tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Panoramica</h2>
        <button onClick={load} className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1.5" data-testid="refresh-overview">
          <RefreshCw size={13} /> Aggiorna
        </button>
      </div>

      {/* Revenue section */}
      <section className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xs font-mono uppercase tracking-widest text-[#C5A059] flex items-center gap-2"><Euro size={12} /> Incassi</h3>
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">Pagamenti in attivazione</span>
        </div>

        {/* Revenue by period */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {periods.map((p) => {
            const d = rev.summary[p.id] || { paid: 0, pending: 0, count: 0 };
            return (
              <div key={p.id} className="border border-zinc-800 bg-zinc-900/40 p-5">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">{p.label}</div>
                <div className="text-2xl text-white font-medium mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{fmtEur(d.paid)}</div>
                <div className="text-[11px] text-zinc-500">
                  incassati
                </div>
                {d.pending > 0 && (
                  <div className="text-[11px] text-[#C5A059] mt-1">
                    {fmtEur(d.pending)} pending
                  </div>
                )}
                <div className="text-[10px] text-zinc-600 mt-1.5">{d.count} transazioni</div>
              </div>
            );
          })}
        </div>

        {/* Chart */}
        <div className="border border-zinc-800 bg-zinc-900/40 p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Andamento incassi · ultimi 30 giorni</div>
            <div className="text-xs text-zinc-400">Totale: {fmtEur(rev.summary.month?.paid)}</div>
          </div>
          {rev.daily_30d.every((d) => d.amount === 0) ? (
            <div className="py-8 text-center text-sm text-zinc-600">Nessun incasso registrato in questo periodo</div>
          ) : (
            <svg viewBox="0 0 100 24" preserveAspectRatio="none" className="w-full h-24">
              {rev.daily_30d.map((d, i) => {
                const h = (d.amount / maxDaily) * 20;
                return (
                  <rect
                    key={i}
                    x={i * barW + barW * 0.15}
                    y={23 - h}
                    width={barW * 0.7}
                    height={h || 0.2}
                    fill="#C5A059"
                    opacity={d.amount > 0 ? 1 : 0.15}
                  >
                    <title>{d.date}: {fmtEur(d.amount)}</title>
                  </rect>
                );
              })}
            </svg>
          )}
          <div className="flex justify-between text-[10px] font-mono text-zinc-600 mt-2">
            <span>{rev.daily_30d[0]?.date}</span>
            <span>oggi</span>
          </div>
        </div>

        {/* Revenue by product */}
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4">Incassi per prodotto</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            {Object.entries(rev.by_product).map(([key, d]) => (
              <div key={key} className="border border-zinc-800 bg-zinc-900/40 p-4">
                <div className="text-[10px] font-mono text-zinc-500 mb-2 leading-tight">{PRODUCT_LABELS[key] || key}</div>
                <div className="text-lg text-white font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{fmtEur(d.paid)}</div>
                <div className="flex items-center justify-between text-[11px] mt-1">
                  <span className="text-zinc-500">{d.count} tx</span>
                  {d.pending > 0 && <span className="text-[#C5A059]">+{fmtEur(d.pending)}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* General KPIs */}
      <section>
        <h3 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4">Attività piattaforma</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {kpis.map((c) => (
            <div key={c.label} className={`border p-5 ${c.alert ? 'border-[#C5A059]/50 bg-[#C5A059]/[0.04]' : 'border-zinc-800 bg-zinc-900/40'}`}>
              <div className="flex items-start justify-between mb-3">
                <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{c.label}</span>
                <c.icon size={14} className={c.accent ? 'text-[#C5A059]' : 'text-zinc-600'} />
              </div>
              <div className="text-3xl text-white font-medium mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{c.value ?? 0}</div>
              <div className="text-[11px] text-zinc-500">{c.sub}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

/* ─── Transactions Tab ─── */
function TransactionsTab() {
  const [txs, setTxs] = useState([]);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter) params.type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      const { data } = await axios.get(`${API}/admin/transactions`, { headers: authHeaders(), params });
      setTxs(data);
    } catch { toast.error('Errore caricamento transazioni'); }
    finally { setLoading(false); }
  }, [typeFilter, statusFilter]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (tx, newStatus) => {
    try {
      await axios.patch(`${API}/admin/transactions/${tx.id}`, { status: newStatus }, { headers: authHeaders() });
      toast.success('Aggiornato');
      load();
    } catch { toast.error('Errore'); }
  };
  const del = async (id) => {
    if (!confirmAction('Eliminare questa transazione?')) return;
    try {
      await axios.delete(`${API}/admin/transactions/${id}`, { headers: authHeaders() });
      toast.success('Eliminata');
      load();
    } catch { toast.error('Errore'); }
  };

  const totals = txs.reduce((a, t) => {
    a.count += 1;
    if (t.status === 'paid') a.paid += t.amount || 0;
    else if (t.status === 'pending_payment') a.pending += t.amount || 0;
    return a;
  }, { count: 0, paid: 0, pending: 0 });

  const TYPE_LABEL = {
    listing_plan: 'Annuncio',
    featured: 'Evidenza',
    loi: 'LOI',
  };

  const statusClass = (s) => ({
    paid: 'bg-emerald-500/10 text-emerald-400',
    pending_payment: 'bg-amber-500/10 text-amber-400',
    refunded: 'bg-red-500/10 text-red-400',
  }[s] || 'bg-zinc-800 text-zinc-500');

  return (
    <div data-testid="admin-transactions">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h2 className="text-2xl text-white tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Transazioni ({totals.count})</h2>
        <div className="flex gap-6 text-xs text-zinc-400">
          <span><strong className="text-white">{fmtEur(totals.paid)}</strong> incassati</span>
          <span className="text-[#C5A059]"><strong>{fmtEur(totals.pending)}</strong> pending</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="bg-zinc-900 border border-zinc-800 text-xs text-zinc-300 px-3 py-2 outline-none focus:border-[#C5A059]" data-testid="tx-type-filter">
          <option value="">Tutti i prodotti</option>
          <option value="listing_plan">Annunci</option>
          <option value="featured">In Evidenza</option>
          <option value="loi">Lettera di Intenti</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-zinc-900 border border-zinc-800 text-xs text-zinc-300 px-3 py-2 outline-none focus:border-[#C5A059]" data-testid="tx-status-filter">
          <option value="">Tutti gli stati</option>
          <option value="paid">Paid</option>
          <option value="pending_payment">Pending</option>
          <option value="refunded">Refunded</option>
        </select>
      </div>

      <div className="border border-zinc-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left p-3">Descrizione</th>
              <th className="text-left p-3">Tipo</th>
              <th className="text-left p-3">Importo</th>
              <th className="text-left p-3">Stato</th>
              <th className="text-left p-3">Data</th>
              <th className="text-right p-3">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-6 text-center text-zinc-500">Caricamento...</td></tr>}
            {!loading && txs.length === 0 && (
              <tr><td colSpan={6} className="p-12 text-center text-zinc-500">
                <CreditCard size={24} className="mx-auto mb-3 opacity-40" />
                <div className="text-sm">Nessuna transazione registrata</div>
                <div className="text-xs mt-1">Le transazioni appariranno quando utenti creano annunci, attivano "In Evidenza" o ordinano LOI.</div>
              </td></tr>
            )}
            {txs.map((tx) => (
              <tr key={tx.id} className="border-t border-zinc-800 hover:bg-zinc-900/30">
                <td className="p-3">
                  <div className="text-white text-sm">{tx.label || tx.type}</div>
                  <div className="text-[11px] text-zinc-500 font-mono">#{(tx.id || '').slice(0, 8).toUpperCase()}</div>
                </td>
                <td className="p-3">
                  <span className="text-[10px] font-mono uppercase tracking-widest bg-zinc-800 text-zinc-300 px-2 py-1">{TYPE_LABEL[tx.type] || tx.type}</span>
                </td>
                <td className="p-3 text-[#C5A059] font-medium">{fmtEur(tx.amount)}</td>
                <td className="p-3">
                  <select value={tx.status} onChange={(e) => setStatus(tx, e.target.value)} className={`text-[11px] font-mono uppercase tracking-widest px-2 py-1 border-0 outline-none cursor-pointer ${statusClass(tx.status)}`} data-testid={`tx-status-${tx.id}`}>
                    <option value="pending_payment">pending</option>
                    <option value="paid">paid</option>
                    <option value="refunded">refunded</option>
                  </select>
                </td>
                <td className="p-3 text-xs text-zinc-500">{fmtDate(tx.created_at)}</td>
                <td className="p-3 text-right">
                  <button onClick={() => del(tx.id)} className="text-zinc-500 hover:text-red-400" title="Elimina" data-testid={`tx-del-${tx.id}`}><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── Users Tab ─── */
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/users`, { headers: authHeaders(), params: q ? { q } : {} });
      setUsers(data);
    } catch { toast.error('Errore caricamento utenti'); }
    finally { setLoading(false); }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const patchUser = async (id, patch) => {
    try {
      await axios.patch(`${API}/admin/users/${id}`, patch, { headers: authHeaders() });
      toast.success('Aggiornato');
      load();
    } catch { toast.error('Errore aggiornamento'); }
  };
  const deleteUser = async (id) => {
    if (!confirmAction('Eliminare utente + suoi annunci/messaggi?')) return;
    try {
      await axios.delete(`${API}/admin/users/${id}`, { headers: authHeaders() });
      toast.success('Utente eliminato');
      load();
    } catch { toast.error('Errore eliminazione'); }
  };

  const sendPasswordReset = async (u) => {
    if (!confirmAction(`Inviare il link per reimpostare la password a ${u.email}?\nIl link scade dopo 1 ora.`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/users/${u.user_id}/send-password-reset`, {}, { headers: authHeaders() });
      toast.success(`Email di reset inviata a ${data.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore invio link reset');
    }
  };

  const setPasswordManually = async (u) => {
    const newPwd = window.prompt(`Imposta una nuova password per ${u.email}\n\nLa password deve avere almeno 6 caratteri. L'utente riceverà un'email di conferma.`, '');
    if (newPwd === null) return; // cancelled
    if (!newPwd || newPwd.length < 6) {
      toast.error('La password deve avere almeno 6 caratteri');
      return;
    }
    if (!confirmAction(`Confermi di voler impostare manualmente la password di ${u.email}?`)) return;
    try {
      await axios.post(
        `${API}/admin/users/${u.user_id}/set-password`,
        { new_password: newPwd, notify_user: true },
        { headers: authHeaders() }
      );
      toast.success(`Password aggiornata per ${u.email}. Comunicagli la nuova password in modo sicuro.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore aggiornamento password');
    }
  };

  return (
    <div data-testid="admin-users">
      <div className="flex items-center justify-between mb-6 gap-4">
        <h2 className="text-2xl text-white tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Utenti ({users.length})</h2>
        <div className="relative w-full max-w-xs">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            placeholder="Cerca email o nome..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-800 pl-9 pr-3 py-2 text-sm text-white outline-none focus:border-[#C5A059]"
            data-testid="users-search"
          />
        </div>
      </div>
      <div className="border border-zinc-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left p-3">Email / Nome</th>
              <th className="text-left p-3">Creato</th>
              <th className="text-right p-3">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={3} className="p-6 text-center text-zinc-500">Caricamento...</td></tr>}
            {!loading && users.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-zinc-500">Nessun utente</td></tr>}
            {users.map((u) => (
              <tr key={u.user_id} className="border-t border-zinc-800 hover:bg-zinc-900/30">
                <td className="p-3">
                  <div className="text-white">{u.full_name || '—'}</div>
                  <div className="text-xs text-zinc-500">{u.email}</div>
                </td>
                <td className="p-3 text-xs text-zinc-500">{fmtDate(u.created_at)}</td>
                <td className="p-3 text-right">
                  <div className="inline-flex items-center gap-2">
                    <button
                      onClick={() => setPasswordManually(u)}
                      className="text-zinc-500 hover:text-amber-400 transition-colors"
                      title="Imposta password manualmente"
                      data-testid={`user-set-password-${u.user_id}`}
                    >
                      <Lock size={15} />
                    </button>
                    <button
                      onClick={() => sendPasswordReset(u)}
                      className="text-zinc-500 hover:text-[#C5A059] transition-colors"
                      title="Invia link reset password via email"
                      data-testid={`user-send-password-reset-${u.user_id}`}
                    >
                      <KeyRound size={15} />
                    </button>
                    <button onClick={() => deleteUser(u.user_id)} className="text-zinc-500 hover:text-red-400 transition-colors" title="Elimina" data-testid={`user-del-${u.user_id}`}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── Listings Tab ─── */
function ClaimEmailCell({ listing, onSave }) {
  const [email, setEmail] = useState(listing.claim_email || '');
  const [lang, setLang] = useState(listing.claim_email_lang || 'it');
  const initialRef = useState({ email: listing.claim_email || '', lang: listing.claim_email_lang || 'it' })[0];
  const dirty = email !== initialRef.email || lang !== initialRef.lang;
  const invitedCount = listing.claim_invited_count || 0;

  return (
    <div className="flex flex-col gap-1 min-w-[230px]">
      <div className="flex gap-1 items-center">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => dirty && onSave(listing, email, lang)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.currentTarget.blur(); }
          }}
          placeholder="email@proprietario.it"
          className="flex-1 bg-zinc-950 border border-zinc-800 px-2 py-1 text-[11px] text-zinc-200 placeholder-zinc-600 outline-none focus:border-[#C5A059] min-w-0"
          data-testid={`claim-email-input-${listing.listing_id}`}
        />
        <select
          value={lang}
          onChange={(e) => { setLang(e.target.value); onSave(listing, email, e.target.value); }}
          className="bg-zinc-950 border border-zinc-800 text-[10px] text-zinc-300 px-1.5 py-1 outline-none focus:border-[#C5A059]"
          data-testid={`claim-lang-select-${listing.listing_id}`}
          title="Lingua email"
        >
          <option value="it">IT</option>
          <option value="en">EN</option>
        </select>
      </div>
      <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-600">
        {invitedCount > 0 ? `✉ invito inviato ×${invitedCount}` : (email ? 'non ancora inviato' : 'nessuna email')}
      </div>
    </div>
  );
}

function ListingsTab() {
  const [listings, setListings] = useState([]);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingListing, setEditingListing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter) params.status = statusFilter;
      const { data } = await axios.get(`${API}/admin/listings-all`, { headers: authHeaders(), params });
      setListings(data);
    } catch { toast.error('Errore caricamento annunci'); }
    finally { setLoading(false); }
  }, [q, statusFilter]);
  useEffect(() => { load(); }, [load]);

  const toggleFeatured = async (l) => {
    try {
      await axios.patch(`${API}/admin/listings/${l.listing_id}`, { featured: !l.featured }, { headers: authHeaders() });
      toast.success(!l.featured ? 'In evidenza (30gg)' : 'Rimosso da evidenza');
      load();
    } catch { toast.error('Errore'); }
  };
  const changeStatus = async (l, status) => {
    try {
      await axios.patch(`${API}/admin/listings/${l.listing_id}`, { status }, { headers: authHeaders() });
      load();
    } catch { toast.error('Errore'); }
  };
  const deleteListing = async (id) => {
    if (!confirmAction('Eliminare annuncio + messaggi/claims collegati?')) return;
    try {
      await axios.delete(`${API}/admin/listings/${id}`, { headers: authHeaders() });
      toast.success('Annuncio eliminato');
      load();
    } catch { toast.error('Errore'); }
  };

  const setClaimEmail = async (l, claim_email, claim_email_lang) => {
    try {
      await axios.patch(
        `${API}/admin/listings/${l.listing_id}`,
        { claim_email: claim_email || '', claim_email_lang: claim_email_lang || l.claim_email_lang || 'it' },
        { headers: authHeaders() }
      );
      toast.success('Email proprietario salvata');
      load();
    } catch { toast.error('Errore salvataggio'); }
  };

  const sendClaimInvite = async (l) => {
    if (!l.claim_email) {
      toast.error('Inserisci prima l\'email del proprietario nella cella a sinistra');
      return;
    }
    if (!confirmAction(`Inviare email "Rivendica il tuo annuncio" a ${l.claim_email}?\nLingua: ${(l.claim_email_lang || 'it').toUpperCase()}`)) return;
    try {
      const { data } = await axios.post(
        `${API}/admin/listings/${l.listing_id}/send-claim-invite`,
        { claim_email: l.claim_email, lang: l.claim_email_lang || 'it' },
        { headers: authHeaders() }
      );
      if (data.success) {
        toast.success(`Email inviata a ${data.to} (ID: ${(data.message_id || '').slice(0, 8)}…)`);
      } else {
        toast.error(`Errore: ${data.error || 'invio fallito'}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore invio invito');
    }
  };

  const changeCreatedAt = async (l) => {
    const current = l.created_at ? new Date(l.created_at) : new Date();
    const iso = current.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm
    const input = window.prompt(`Modifica data pubblicazione (formato: YYYY-MM-DD HH:mm)\nAttuale: ${iso.replace('T', ' ')}`, iso.replace('T', ' '));
    if (!input) return;
    // Accept both "YYYY-MM-DD HH:mm" and "YYYY-MM-DDTHH:mm"
    const normalized = input.trim().replace(' ', 'T');
    const d = new Date(normalized);
    if (Number.isNaN(d.getTime())) {
      toast.error('Data non valida. Usa formato YYYY-MM-DD HH:mm');
      return;
    }
    try {
      await axios.patch(`${API}/admin/listings/${l.listing_id}`, { created_at: d.toISOString() }, { headers: authHeaders() });
      toast.success('Data pubblicazione aggiornata');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore aggiornamento data');
    }
  };

  const resetClaim = async (l) => {
    if (!confirmAction(`Ripristinare "${l.manufacturer} ${l.model}" come annuncio non rivendicato?\n\nQuesto rimuoverà il proprietario attuale e permetterà di inviare nuovamente l'invito di rivendicazione.`)) return;
    try {
      await axios.post(
        `${API}/admin/listings/${l.listing_id}/reset-claim`,
        {},
        { headers: authHeaders() }
      );
      toast.success('Annuncio ripristinato come non rivendicato');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore durante il ripristino');
    }
  };

  return (
    <div data-testid="admin-listings">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h2 className="text-2xl text-white tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Annunci ({listings.length})</h2>
        <div className="flex gap-3">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-zinc-900 border border-zinc-800 text-xs text-zinc-300 px-3 py-2 outline-none focus:border-[#C5A059]" data-testid="listings-status-filter">
            <option value="">Tutti gli stati</option>
            <option value="published">Pubblicato</option>
            <option value="expired">Scaduto</option>
            <option value="draft">Bozza</option>
            <option value="paused">In pausa</option>
          </select>
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input type="text" placeholder="Cerca titolo o marca..." value={q} onChange={(e) => setQ(e.target.value)} className="bg-zinc-900 border border-zinc-800 pl-9 pr-3 py-2 text-sm text-white outline-none focus:border-[#C5A059]" data-testid="listings-search" />
          </div>
        </div>
      </div>
      <div className="border border-zinc-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left p-3">Annuncio</th>
              <th className="text-left p-3">Categoria</th>
              <th className="text-left p-3">Prezzo</th>
              <th className="text-left p-3">Stato</th>
              <th className="text-left p-3">Featured</th>
              <th className="text-left p-3">Proprietario / Claim</th>
              <th className="text-right p-3">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-6 text-center text-zinc-500">Caricamento...</td></tr>}
            {!loading && listings.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-zinc-500">Nessun annuncio</td></tr>}
            {listings.map((l) => (
              <tr key={l.listing_id} className="border-t border-zinc-800 hover:bg-zinc-900/30">
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    {l.images?.[0] ? (
                      <img
                        src={l.images[0]}
                        alt=""
                        className="w-16 h-12 object-cover border border-zinc-800 bg-zinc-900"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <div className={`w-16 h-12 bg-zinc-900 border border-zinc-800 items-center justify-center text-zinc-700 ${l.images?.[0] ? 'hidden' : 'flex'}`}>
                      <PlaneIcon size={14} />
                    </div>
                    <div className="min-w-0">
                      <a
                        href={l.slug ? `/listings/${l.slug}` : `/listings/${l.listing_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-white text-sm hover:text-[#C5A059] transition-colors block truncate"
                        title="Apri annuncio in una nuova scheda"
                        data-testid={`listing-view-${l.listing_id}`}
                      >
                        {l.title || '—'}
                      </a>
                      <div className="text-[11px] text-zinc-500 font-mono uppercase tracking-wider">{l.registration || '—'}</div>
                    </div>
                  </div>
                </td>
                <td className="p-3 text-xs text-zinc-400">{l.category}</td>
                <td className="p-3 text-[#C5A059]">{fmtPrice(l.price)}</td>
                <td className="p-3">
                  <select value={l.status || 'published'} onChange={(e) => changeStatus(l, e.target.value)} className="bg-zinc-950 border border-zinc-800 text-xs text-zinc-300 px-2 py-1 outline-none focus:border-[#C5A059]" data-testid={`listing-status-${l.listing_id}`}>
                    <option value="published">published</option>
                    <option value="expired">expired</option>
                    <option value="draft">draft</option>
                    <option value="paused">paused</option>
                  </select>
                </td>
                <td className="p-3">
                  <button onClick={() => toggleFeatured(l)} className={`text-xs px-2.5 py-1 border transition-colors ${l.featured ? 'border-[#C5A059] text-[#C5A059] bg-[#C5A059]/10' : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'}`} data-testid={`listing-feat-${l.listing_id}`}>
                    {l.featured ? '★ ON' : '☆ off'}
                  </button>
                </td>
                <td className="p-3">
                  {l.claimable === false ? (
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[11px] text-emerald-400 truncate max-w-[220px]" title={l.claim_email}>
                        {l.claim_email || '—'}
                      </span>
                      <span className="text-[9px] font-mono uppercase tracking-widest text-emerald-400/70">
                        ✓ rivendicato
                      </span>
                    </div>
                  ) : (
                    <ClaimEmailCell listing={l} onSave={setClaimEmail} />
                  )}
                </td>
                <td className="p-3 text-right">
                  <div className="inline-flex gap-2 items-center">
                    {l.claimable !== false && (
                      <button
                        onClick={() => sendClaimInvite(l)}
                        className="text-zinc-500 hover:text-[#C5A059] transition-colors"
                        title={l.claim_email ? 'Reinvia invito rivendicazione' : 'Invia invito rivendicazione'}
                        data-testid={`listing-claim-invite-${l.listing_id}`}
                      >
                        <Send size={15} />
                      </button>
                    )}
                    {l.claimable === false && (
                      <button
                        onClick={() => resetClaim(l)}
                        className="text-zinc-500 hover:text-amber-400 transition-colors"
                        title="Ripristina come non rivendicato"
                        data-testid={`listing-reset-claim-${l.listing_id}`}
                      >
                        <RotateCcw size={15} />
                      </button>
                    )}
                    <button
                      onClick={() => changeCreatedAt(l)}
                      className="text-zinc-500 hover:text-[#C5A059] transition-colors"
                      title={`Modifica data pubblicazione (${l.created_at ? new Date(l.created_at).toLocaleDateString('it-IT') : '—'})`}
                      data-testid={`listing-edit-date-${l.listing_id}`}
                    >
                      <CalendarDays size={15} />
                    </button>
                    <button
                      onClick={() => setEditingListing(l)}
                      className="text-zinc-500 hover:text-[#C5A059] transition-colors"
                      title="Modifica annuncio (silenzioso, no email al proprietario)"
                      data-testid={`listing-edit-${l.listing_id}`}
                    >
                      <Edit size={15} />
                    </button>
                    <button onClick={() => deleteListing(l.listing_id)} className="text-zinc-500 hover:text-red-400" title="Elimina" data-testid={`listing-del-${l.listing_id}`}><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editingListing && (
        <AdminEditListingDialog
          listing={editingListing}
          onClose={() => setEditingListing(null)}
          onSaved={() => { setEditingListing(null); load(); }}
        />
      )}
    </div>
  );
}

/* ─── Admin-only Listing Edit Dialog (silent: no owner notification) ─── */
function AdminEditListingDialog({ listing, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: listing.title || '',
    manufacturer: listing.manufacturer || '',
    model: listing.model || '',
    year: listing.year || '',
    registration: listing.registration || '',
    price: listing.price || '',
    location: listing.location || '',
    description: listing.description || '',
    engine_type: listing.engine_type || '',
    engine_hours: listing.engine_hours || '',
    seats: listing.seats || '',
    avionics: listing.avionics || '',
    contact_email: listing.contact_email || '',
    contact_phone: listing.contact_phone || '',
  });
  const [saving, setSaving] = useState(false);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== '' && v !== null));
      if (body.year) body.year = parseInt(body.year, 10);
      if (body.price) body.price = parseFloat(body.price);
      if (body.engine_hours) body.engine_hours = parseFloat(body.engine_hours);
      if (body.seats) body.seats = parseInt(body.seats, 10);
      await axios.put(`${API}/listings/${listing.listing_id}`, body, { headers: authHeaders() });
      toast.success('Annuncio aggiornato (proprietario NON notificato)');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore aggiornamento');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="admin-edit-dialog">
      <div className="bg-zinc-950 border border-zinc-800 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-zinc-950 border-b border-zinc-800 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h3 className="text-xl text-white tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Modifica annuncio (Admin)</h3>
            <p className="text-[11px] font-mono uppercase tracking-widest text-amber-400 mt-1">Silenzioso · Nessuna email al proprietario</p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="admin-edit-close"><X size={18} /></button>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ['Titolo','title','text','md:col-span-2'],
            ['Costruttore','manufacturer','text',''],
            ['Modello','model','text',''],
            ['Anno','year','number',''],
            ['Marche','registration','text',''],
            ['Prezzo (EUR)','price','number',''],
            ['Località','location','text',''],
            ['Tipo motore','engine_type','text',''],
            ['Ore motore','engine_hours','number',''],
            ['Posti','seats','number',''],
            ['Avionica','avionics','text','md:col-span-2'],
            ['Email contatto','contact_email','email',''],
            ['Telefono contatto','contact_phone','tel',''],
          ].map(([label, key, type, span]) => (
            <div key={key} className={span}>
              <label className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">{label}</label>
              <input
                type={type}
                value={form[key]}
                onChange={(e) => setField(key, e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm px-3 py-2 outline-none focus:border-[#C5A059]"
                data-testid={`admin-edit-${key}`}
              />
            </div>
          ))}
          <div className="md:col-span-2">
            <label className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">Descrizione</label>
            <textarea
              rows={6}
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm px-3 py-2 outline-none focus:border-[#C5A059] whitespace-pre-wrap"
              data-testid="admin-edit-description"
            />
          </div>
        </div>
        <div className="sticky bottom-0 bg-zinc-950 border-t border-zinc-800 px-6 py-4 flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-zinc-400 hover:text-white" data-testid="admin-edit-cancel">Annulla</button>
          <button onClick={save} disabled={saving} className="bg-[#C5A059] text-black px-5 py-2 text-sm font-medium hover:bg-[#D4B271] disabled:opacity-50 transition-colors" data-testid="admin-edit-save">
            {saving ? 'Salvataggio…' : 'Salva modifiche'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Claims Tab ─── */
function ClaimsTab() {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/claims`, { headers: authHeaders() });
      setClaims(data);
    } catch { toast.error('Errore'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, action) => {
    try {
      await axios.post(`${API}/admin/claims/${id}/decision`, { action }, { headers: authHeaders() });
      toast.success(action === 'approve' ? 'Rivendicazione approvata' : 'Rivendicazione rifiutata');
      load();
    } catch { toast.error('Errore'); }
  };

  return (
    <div data-testid="admin-claims">
      <h2 className="text-2xl text-white tracking-tight mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Rivendicazioni ({claims.length})</h2>
      <div className="border border-zinc-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left p-3">Richiedente</th>
              <th className="text-left p-3">Annuncio</th>
              <th className="text-left p-3">Messaggio</th>
              <th className="text-left p-3">Stato</th>
              <th className="text-left p-3">Data</th>
              <th className="text-right p-3">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-6 text-center text-zinc-500">Caricamento...</td></tr>}
            {!loading && claims.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-zinc-500">Nessuna rivendicazione</td></tr>}
            {claims.map((c) => (
              <tr key={c.claim_id} className="border-t border-zinc-800 hover:bg-zinc-900/30 align-top">
                <td className="p-3">
                  <div className="text-white text-sm">{c.claimer_name || '—'}</div>
                  <div className="text-[11px] text-zinc-500">{c.claimer_email}</div>
                </td>
                <td className="p-3 text-xs text-zinc-400">{c.listing_title || c.listing_id}</td>
                <td className="p-3 text-xs text-zinc-400 max-w-md">{c.message || c.proof_text || '—'}</td>
                <td className="p-3">
                  <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 ${c.status === 'pending' ? 'bg-amber-500/10 text-amber-400' : c.status === 'approved' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-zinc-800 text-zinc-500'}`}>{c.status}</span>
                </td>
                <td className="p-3 text-xs text-zinc-500">{fmtDate(c.created_at)}</td>
                <td className="p-3 text-right">
                  {c.status === 'pending' && (
                    <div className="inline-flex gap-2">
                      <button onClick={() => decide(c.claim_id, 'approve')} className="text-emerald-400 hover:text-emerald-300" title="Approva" data-testid={`claim-approve-${c.claim_id}`}><Check size={16} /></button>
                      <button onClick={() => decide(c.claim_id, 'reject')} className="text-red-400 hover:text-red-300" title="Rifiuta" data-testid={`claim-reject-${c.claim_id}`}><X size={16} /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── Requests Tab ─── */
function RequestsTab() {
  const [items, setItems] = useState([]);
  const [tipo, setTipo] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = tipo ? { tipo } : {};
      const { data } = await axios.get(`${API}/admin/requests`, { headers: authHeaders(), params });
      setItems(data);
    } catch { toast.error('Errore'); } finally { setLoading(false); }
  }, [tipo]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (it, newStatus) => {
    try {
      await axios.patch(`${API}/admin/requests/${it.kind}/${it.id}`, { status: newStatus }, { headers: authHeaders() });
      toast.success('Aggiornato');
      load();
    } catch { toast.error('Errore'); }
  };
  const del = async (it) => {
    if (!confirmAction('Eliminare definitivamente questa richiesta?')) return;
    try {
      await axios.delete(`${API}/admin/requests/${it.kind}/${it.id}`, { headers: authHeaders() });
      toast.success('Eliminata');
      load();
    } catch { toast.error('Errore'); }
  };

  const exportCSV = () => {
    const rows = [
      ['tipo', 'oggetto', 'nome', 'email', 'status', 'data', 'messaggio'],
      ...items.map((i) => [i.kind, i.oggetto, i.nome, i.email, i.status, i.created_at, (i.messaggio || '').replace(/\n/g, ' ')]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `aerodeals-richieste-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const FILTERS = [
    { id: '', label: 'Tutte' },
    { id: 'consulenze', label: 'Consulenze' },
    { id: 'piano', label: 'Piani Acquisizione' },
    { id: 'pubblicita', label: 'Spazio Pubblicitario' },
    { id: 'loi', label: 'Ordini LOI' },
  ];

  return (
    <div data-testid="admin-requests">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h2 className="text-2xl text-white tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Richieste ({items.length})</h2>
        <button onClick={exportCSV} className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1.5" data-testid="export-csv"><Download size={13} /> Export CSV</button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {FILTERS.map((f) => (
          <button key={f.id} onClick={() => setTipo(f.id)} className={`text-xs px-3 py-1.5 border transition-colors ${tipo === f.id ? 'border-[#C5A059] bg-[#C5A059]/10 text-[#C5A059]' : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'}`} data-testid={`filter-${f.id || 'all'}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="border border-zinc-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left p-3">Oggetto</th>
              <th className="text-left p-3">Contatto</th>
              <th className="text-left p-3">Messaggio</th>
              <th className="text-left p-3">Stato</th>
              <th className="text-left p-3">Data</th>
              <th className="text-right p-3">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-6 text-center text-zinc-500">Caricamento...</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-zinc-500">Nessuna richiesta</td></tr>}
            {items.map((it) => (
              <tr key={`${it.kind}-${it.id}`} className="border-t border-zinc-800 hover:bg-zinc-900/30 align-top">
                <td className="p-3">
                  <div className="text-white text-sm">{it.oggetto}</div>
                  <div className="text-[10px] font-mono text-zinc-600 mt-0.5">{it.kind === 'loi' ? 'ORDER' : 'REQUEST'}</div>
                </td>
                <td className="p-3">
                  <div className="text-white text-sm">{it.nome || '—'}</div>
                  <div className="text-[11px] text-zinc-500"><a href={`mailto:${it.email}`} className="hover:text-[#C5A059]">{it.email}</a></div>
                </td>
                <td className="p-3 text-xs text-zinc-400 max-w-md whitespace-pre-wrap">{it.messaggio || '—'}</td>
                <td className="p-3">
                  <select value={it.status} onChange={(e) => setStatus(it, e.target.value)} className="bg-zinc-950 border border-zinc-800 text-xs text-zinc-300 px-2 py-1 outline-none focus:border-[#C5A059]" data-testid={`req-status-${it.id}`}>
                    {it.kind === 'loi' ? (
                      <>
                        <option value="pending_payment">pending_payment</option>
                        <option value="paid">paid</option>
                        <option value="delivered">delivered</option>
                        <option value="archived">archived</option>
                      </>
                    ) : (
                      <>
                        <option value="new">new</option>
                        <option value="in_progress">in_progress</option>
                        <option value="handled">handled</option>
                        <option value="archived">archived</option>
                      </>
                    )}
                  </select>
                </td>
                <td className="p-3 text-xs text-zinc-500">{fmtDate(it.created_at)}</td>
                <td className="p-3 text-right">
                  <button onClick={() => del(it)} className="text-zinc-500 hover:text-red-400" title="Elimina" data-testid={`req-del-${it.id}`}><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── Emails Tab ─── */
function EmailsTab() {
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState('welcome');
  const [meta, setMeta] = useState(null);
  const [lang, setLang] = useState('it');

  useEffect(() => {
    axios.get(`${API}/admin/emails/list`, { headers: authHeaders() })
      .then((r) => setList(r.data))
      .catch(() => toast.error('Errore caricamento template'));
  }, []);

  useEffect(() => {
    if (!selected) return;
    axios.get(`${API}/admin/emails/subject/${selected}?lang=${lang}`, { headers: authHeaders() })
      .then((r) => setMeta(r.data))
      .catch(() => setMeta(null));
  }, [selected, lang]);

  const token = localStorage.getItem(TOKEN_KEY);
  const previewSrc = selected && token ? `${API}/admin/emails/preview/${selected}?token=${encodeURIComponent(token)}&lang=${lang}` : '';

  return (
    <div data-testid="admin-emails">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl text-white tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Email Template</h2>
          <p className="text-xs text-zinc-500 mt-1">{list.length} template attivi — localizzati IT/EN.</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Language toggle */}
          <div className="inline-flex border border-zinc-700 overflow-hidden" data-testid="email-lang-toggle">
            {['it', 'en'].map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest transition-colors ${lang === l ? 'bg-[#C5A059] text-black' : 'bg-transparent text-zinc-400 hover:text-white'}`}
                data-testid={`email-lang-${l}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-5">
        {/* Sidebar list */}
        <div className="border border-zinc-800 bg-zinc-900/40 p-2 max-h-[700px] overflow-y-auto">
          {list.map((t) => (
            <button
              key={t.key}
              onClick={() => setSelected(t.key)}
              className={`w-full text-left px-3 py-2.5 text-sm transition-colors ${selected === t.key ? 'bg-[#C5A059]/10 text-[#C5A059] border-l-2 border-[#C5A059]' : 'text-zinc-400 hover:bg-zinc-800 hover:text-white border-l-2 border-transparent'}`}
              data-testid={`email-tpl-${t.key}`}
            >
              <div className="font-medium">{t.label}</div>
              <div className="text-[10px] font-mono text-zinc-600 mt-0.5">{t.key}</div>
            </button>
          ))}
        </div>

        {/* Preview */}
        <div>
          {meta && (
            <div className="border border-zinc-800 bg-zinc-900/40 p-4 mb-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Subject</div>
              <div className="text-white text-sm font-medium mb-3">{meta.subject}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Plain text preview</div>
              <pre className="text-[11px] text-zinc-500 whitespace-pre-wrap font-mono leading-relaxed">{meta.text_preview}</pre>
            </div>
          )}
          <div className="border border-zinc-800 bg-white overflow-hidden">
            {previewSrc && (
              <iframe
                src={previewSrc}
                title="Email preview"
                className="w-full"
                style={{ height: '720px', border: 0 }}
                data-testid="email-preview-iframe"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Placeholder Tab ─── */function Placeholder({ title, desc, icon: Icon }) {
  return (
    <div className="border border-dashed border-zinc-800 p-12 text-center" data-testid={`placeholder-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <Icon size={32} className="text-[#C5A059] mx-auto mb-4 opacity-60" />
      <h2 className="text-xl text-white mb-2 tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{title}</h2>
      <p className="text-sm text-zinc-500 max-w-md mx-auto leading-relaxed">{desc}</p>
      <div className="mt-6 inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest bg-[#C5A059]/10 text-[#C5A059] px-3 py-1.5">
        <Clock size={10} /> In sviluppo · Fase 2
      </div>
    </div>
  );
}

/* ─── Main Admin Shell ─── */
export default function Admin() {
  useNoIndex('AeroDeals Admin');
  const [authed, setAuthed] = useState(() => !!localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    // Hide header/footer when admin is open
    document.body.classList.add('admin-mode');
    return () => document.body.classList.remove('admin-mode');
  }, []);

  // Intercept 401s → log out
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 401 && err?.config?.url?.includes('/admin/') && !err?.config?.url?.includes('/admin/login')) {
          localStorage.removeItem(TOKEN_KEY);
          setAuthed(false);
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  if (!authed) return <LoginGate onAuth={() => setAuthed(true)} />;

  const renderTab = () => {
    switch (tab) {
      case 'overview': return <OverviewTab />;
      case 'transactions': return <TransactionsTab />;
      case 'users': return <UsersTab />;
      case 'listings': return <ListingsTab />;
      case 'claims': return <ClaimsTab />;
      case 'requests': return <RequestsTab />;
      case 'emails': return <EmailsTab />;
      case 'chrono': return <ChronoAndamentoTab onNavigate={setTab} />;
      case 'andamento': return <ChronoAndamentoTab onNavigate={setTab} />;
      case 'audit': return <AuditTab />;
      case 'blog': return <BlogPostsTab />;
      case 'seo': return <SEOPagesTab />;
      case 'content-queue': return <ContentQueueTab />;
      case 'plan-generator': return <PlanGeneratorTab />;
      case 'chrono-cron': return <ChronoCronTab />;
      case 'redirects': return <ChronoRedirectsTab />;
      case 'entities': return <EntitiesTab />;
      case 'scraper': return <Placeholder title="Scraper Esterni" desc="Import automatico di annunci da altri marketplace (controller.com, trade-a-plane, ecc.). Richiede definizione delle sorgenti target e mapping dei campi." icon={Download} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex" data-testid="admin-panel">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:static z-30 w-64 h-screen bg-zinc-950 border-r border-zinc-800 flex flex-col transition-transform`}>
        <div className="p-5 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Shield size={16} className="text-[#C5A059]" />
            <span className="text-sm font-medium text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>AeroDeals Admin</span>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 mt-1">Control Center</div>
        </div>
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {TABS.map((t) => {
            if (t.section) {
              return (
                <div key={t.id} className="flex items-center gap-2 px-3 pt-4 pb-1 text-[9px] font-mono uppercase tracking-widest text-zinc-600">
                  <t.icon size={11} /> {t.label}
                </div>
              );
            }
            if (t.advancedToggle) {
              return (
                <button
                  key={t.id}
                  onClick={() => setShowAdvanced(v => !v)}
                  className="w-full flex items-center gap-3 px-3 py-2 text-xs text-zinc-500 hover:text-white transition-colors border-l-2 border-transparent"
                  data-testid="toggle-advanced"
                >
                  <t.icon size={13} />
                  <span className="flex-1 text-left font-mono uppercase tracking-widest">{showAdvanced ? 'Avanzato ▾' : 'Avanzato ▸'}</span>
                </button>
              );
            }
            if (t.advanced && !showAdvanced) return null;
            return (
              <button
                key={t.id}
                onClick={() => { setTab(t.id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${tab === t.id ? 'bg-[#C5A059]/10 text-[#C5A059] border-l-2 border-[#C5A059]' : 'text-zinc-400 hover:bg-zinc-900 hover:text-white border-l-2 border-transparent'} ${t.advanced ? 'pl-6' : ''}`}
                data-testid={`tab-${t.id}`}
              >
                <t.icon size={15} />
                <span className="flex-1 text-left">{t.label}</span>
                {t.soon && <span className="text-[9px] font-mono uppercase bg-zinc-800 text-zinc-500 px-1.5 py-0.5">SOON</span>}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-zinc-800 space-y-0.5">
          <a href="/" className="w-full flex items-center gap-3 px-3 py-2 text-sm text-zinc-500 hover:text-white transition-colors">
            <ExternalLink size={14} /> Torna al sito
          </a>
          <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-zinc-500 hover:text-red-400 transition-colors" data-testid="admin-logout">
            <LogOut size={14} /> Esci
          </button>
        </div>
      </aside>

      {/* Mobile toggle */}
      <button onClick={() => setSidebarOpen((o) => !o)} className="lg:hidden fixed top-4 left-4 z-40 bg-zinc-900 border border-zinc-800 p-2 text-white">
        <Filter size={16} />
      </button>

      {/* Main content */}
      <main className="flex-1 p-6 md:p-10 overflow-x-hidden">
        {renderTab()}
      </main>
    </div>
  );
}
